import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Category, CreateCategory, UpdateCategory } from '@thro-types/leihbar';

import {
  createLeihbarCategory,
  deleteLeihbarCategory,
  getLeihbarCategories,
  updateLeihbarCategory
} from 'api';
import { AxiosRequestConfig } from 'axios';

/**
 * A custom hook for leihbar categories
 *
 * @returns DefinedUseQueryResult<Category[], Error>
 */
export const useLeihbarCategoriesQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['leihbarCategories'],
    queryFn: () => {
      return getLeihbarCategories(config);
    }
  });

  return query;
};

export const useLeihbarCategoriesMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();
  const queryKey = ['leihbarCategories'];

  const create = useMutation<Category, Error, CreateCategory>({
    mutationFn: async args => {
      return await createLeihbarCategory(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const update = useMutation<Category, Error, { id: number; data: UpdateCategory }>({
    mutationFn: async args => {
      return await updateLeihbarCategory(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const deletion = useMutation<Category, Error, number>({
    mutationFn: async args => {
      return await deleteLeihbarCategory(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { create, update, deletion } as const;
};
